import { cx } from '@/lib/cx'

// The 8 admissions states from design-system.md's status palette.
// Keys match the CSS custom properties in tokens.css.
export const APPLICATION_STATUSES = [
  'draft',
  'submitted',
  'under-review',
  'interview',
  'accepted',
  'waitlisted',
  'rejected',
  'withdrawn',
] as const

export type ApplicationStatus = (typeof APPLICATION_STATUSES)[number]

const LABELS: Record<ApplicationStatus, string> = {
  'draft': 'Draft',
  'submitted': 'Submitted',
  'under-review': 'Under Review',
  'interview': 'Interview',
  'accepted': 'Accepted',
  'waitlisted': 'Waitlisted',
  'rejected': 'Rejected',
  'withdrawn': 'Withdrawn',
}

// The backend's raw status enum values (see Application#status, e.g.
// "under_review") don't all line up with this palette's hyphenated keys, and
// include values with no token here at all (e.g. "deferred"). Normalize and
// return null rather than throwing when there's no matching token, so
// callers can fall back to a plain, un-tinted — but still labeled — chip.
export function toChipStatus(status: string): ApplicationStatus | null {
  const normalized = status.replace(/_/g, '-')
  return (APPLICATION_STATUSES as readonly string[]).includes(normalized) ? (normalized as ApplicationStatus) : null
}

type StatusChipProps = {
  status: ApplicationStatus
  className?: string
}

// Status is never encoded by color alone: the chip always carries its label.
export function StatusChip({ status, className }: StatusChipProps) {
  return (
    <span
      data-status={status}
      className={cx('chip', className)}
      style={{
        ['--chip-bg' as string]: `var(--status-${status}-bg)`,
        ['--chip-dot' as string]: `var(--status-${status}-dot)`,
      }}
    >
      <span className="chip__dot" aria-hidden="true" />
      {LABELS[status]}
    </span>
  )
}
